<template>
    <div class="message-center-footer w-90% flex items-end w-full gap-10px py-20px flex-shrink-0"
        :style="{ position: 'fixed', bottom: distanceToViewportBottom + 'px', zIndex: 99, left: '50%', transform: 'translateX(-50%)', gap: isFocusing ? '0px' : '10px' }"
        :class="{ 'expanded-input-container': isExpanded }">
        <!-- 切换场景按钮 -->
        <div v-if="!isShowFree">
            <div class="w-38px h-38px relative" @click="$emit('switchScene')"
                v-if="sceneList.length > 1 && !isFocusing">
                <img src="@/assets/images/message/switch.svg" alt="" class="w-full h-full">
                <Transition name="shake-fade">
                    <div v-if="showChangeSceneTip" class="absolute top--42px rounded-10px left-0px w-103px h-34px">
                        <div class="bg-#fff text-12px font-500 rounded-10px text-center leading-34px color-#000">
                            {{ t('scene.changeScene') }}
                        </div>
                        <div class="absolute bottom-1px left-10px w-10px h-10px">
                            <img src="@/assets/images/message/down.svg" alt="" class="w-full h-full">
                        </div>
                    </div>
                </Transition>
            </div>
        </div>

        <!-- 输入框容器 -->
        <div class="message-center-footer-input flex w-full items-end relative" :class="[
            { 'active-within': isFocus },
            { 'focusing-input': isFocusing },
            { 'backdrop-ready': isBackdropReady },
            { 'expanded-input': isExpanded }
        ]" ref="inputWrapperRef" @click="inputRef?.focus()">

            <!-- 插入星号按钮 -->
            <div class="w-20px h-25px flex items-center mr-4px justify-center" v-if="!isFocusing"
                @click.stop.prevent="handleInsertAsterisk" @touchend.stop.prevent="handleInsertAsterisk"
                :class="{ 'is-focusing': isFocusing }">
                <img src="@/assets/images/message/input_*.svg" class="w-full h-full" alt=""
                    style="pointer-events: none;" />

            </div>
            <textarea v-model="localMessage" type="textarea"
                :placeholder="!disabled ? t('message.chat_placeholder') : t('message.chatMateTyping')" rows="1"
                @input="autoResize" @keydown.enter.exact="handleSend" @keydown.enter.shift.exact.prevent="handleNewLine"
                class="message-center-footer-input-textarea w-100%" ref="inputRef" :disabled="disabled"
                :readonly="disabled" enterkeyhint="send" @blur="handleBlur()" @focus="handleFocus">
            </textarea>
            <!-- 快捷回复 -->
            <!-- <div @click.stop.prevent="handleQuickReply" @touchend.stop.prevent="handleQuickReply"
                class="mb-2px w-20px h-20px flex items-center justify-center" v-if="!isFocusing">
                <img src="@/assets/images/chat/chatRecord/denglong.svg" class="w-full h-full" alt="" />
            </div> -->
            <!-- 发送按钮 -->
            <div v-if="!isFocusing">
                <div v-if="localMessage.length > 0 && !disabled"
                    class="ml-10px w-25px h-25px flex items-center mt-0px relative">
                    <img src="@/assets/images/message/send.svg" class="w-full h-full" alt=""
                        style="pointer-events: none;" />
                    <div class="absolute top--0px right--0px w-40px h-40px" @click.stop.prevent="handleSend"
                        @touchend.stop.prevent="handleSend"></div>
                </div>
            </div>
            <div class="flex items-center justify-between w-100% h-25px" v-if="isFocusing" @mousedown.prevent
                @touchstart.prevent>
                <div class="flex items-center">
                    <div class="w-20px h-25px flex items-center mr-4px justify-center"
                        @click.stop.prevent="handleInsertAsterisk" @touchend.stop.prevent="handleInsertAsterisk">
                        <img src="@/assets/images/message/input_*.svg" class="w-full h-full" alt=""
                            style="pointer-events: none;" />
                        <!-- 展开按钮：只有内容超过两行时显示 -->
                    </div>
                    <div v-if="isExpanded || isContentOverTwoLines" @click.stop.prevent="toggleExpand"
                        @touchend.stop.prevent="toggleExpand"
                        class="ml-10px w-20px h-20px flex items-center justify-center">
                        <img v-if="!isExpanded" src="@/assets/images/message/unfold_input.svg" class="w-full h-full"
                            alt="" />
                        <img v-else src="@/assets/images/message/collapse.svg" class="w-full h-full" alt="" />
                    </div>
                </div>
                <div class="flex items-center justify-center">
                    <!-- 快捷回复 -->
                    <!-- <div @click.stop.prevent="handleQuickReply" @touchend.stop.prevent="handleQuickReply"
                        class="mr-10px w-20px h-20px flex items-center justify-center">
                        <img src="@/assets/images/chat/chatRecord/denglong.svg" class="w-full h-full" alt="" />
                    </div> -->
                    <!-- 发送按钮：有内容时显示 -->
                    <div v-if="localMessage.length > 0 && !disabled"
                        class="w-25px h-25px flex items-center mt-0px relative">
                        <img src="@/assets/images/message/send.svg" class="w-full h-full" alt=""
                            style="pointer-events: none;" />
                        <div class="absolute top--0px right--0px w-25px h-25px" @click.stop.prevent="handleSend"
                            @touchend.stop.prevent="handleSend"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { isFreeRole } from '@/utils/publicMethod'
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useChatStore } from '@/stores/chat'
import { logEventSafe } from '@/plugins/firebase'

interface Props {
    disabled?: boolean
    distanceToViewportBottom?: number
    showChangeSceneTip?: boolean
    isEmbeddedH5?: boolean
    sceneList?: any[]
    isBackdropReady?: boolean
    info: any
    isShowFree: boolean
}

const props = withDefaults(defineProps<Props>(), {
    info: {},
    disabled: false,
    distanceToViewportBottom: 0,
    showChangeSceneTip: false,
    isEmbeddedH5: false,
    sceneList: () => [],
    isBackdropReady: false,
    isShowFree: false
})

const emit = defineEmits<{
    (e: 'send', message: string): void
    (e: 'switchScene'): void
    (e: 'keyboardShow'): void
    (e: 'keyboardHide'): void
    (e: 'updateDistance', value: number): void
    (e: 'handleQuickReply'): void
}>()

const { t } = useI18n()
const chatStore = useChatStore()

const inputRef = ref<HTMLTextAreaElement>()
const inputWrapperRef = ref<HTMLElement>()
const isFocus = ref(false)
const placeholderText = ref(t('message.chat_placeholder'))
const isFocusing = ref(false)
const isContentOverTwoLines = ref(false) // 内容是否超过两行
const isExpanded = ref(false) // 是否展开显示全部内容

// 当前消息内容
const localMessage = computed({
    get: () => chatStore.currentMessage,
    set: (value: string) => chatStore.setCurrentMessage(value)
})

// 根据内容自动扩展高度
const autoResize = () => {
    const el = inputRef.value
    if (!el) return

    const minHeightWhenFocusing = 50 // 聚焦时的最小高度
    const twoLinesHeight = 50 // 两行的大致高度（fontSize 14px * lineHeight 1.4 * 2 ≈ 40-50px）
    const expandedHeight = 300 // 展开时的固定高度
    const collapsedMaxHeight = 145 // 收起时最多显示约 5-6 行

    el.style.height = 'auto'
    const contentHeight = el.scrollHeight

    // 判断内容是否超过两行（用于显示展开按钮）
    isContentOverTwoLines.value = contentHeight > twoLinesHeight

    // 如果展开状态，固定高度为 300px
    if (isExpanded.value) {
        el.style.height = expandedHeight + 'px'
        el.style.overflowY = 'auto'
        return
    }

    // 未展开状态：根据内容自动调整
    let targetHeight = contentHeight
    let maxHeight = collapsedMaxHeight

    // 如果正在聚焦状态，确保最小高度为 80px
    if (isFocusing.value && contentHeight < minHeightWhenFocusing) {
        targetHeight = minHeightWhenFocusing
    }

    el.style.height = Math.min(targetHeight, maxHeight) + 'px'

    if (contentHeight > maxHeight) {
        el.style.overflowY = 'auto'
    } else {
        el.style.overflowY = 'hidden'
    }
}

// 重置输入框高度
const resetHeight = () => {
    const el = inputRef.value
    if (!el) return
    el.style.height = '25px'
    el.style.overflowY = 'hidden'
}

// 发送消息
const handleSend = () => {
    if (!localMessage.value.trim() || props.disabled) {
        return
    }

    const message = localMessage.value.trim()

    // 立即收起键盘
    if (inputRef.value) {
        inputRef.value.blur()
    }

    emit('send', message)

    // 清空后重置高度
    nextTick(() => {
        resetHeight()
    })
}

// 处理换行
const handleNewLine = () => {
    localMessage.value += '\n'
    nextTick(() => {
        autoResize()
    })
}

// 快捷回复
const handleQuickReply = () => {
    console.log('handleQuickReply called')
    emit('handleQuickReply')
}

// 插入星号
const handleInsertAsterisk = () => {
    console.log('handleInsertAsterisk called')
    const textarea = inputRef.value
    if (!textarea) {
        console.log('textarea not found')
        return
    }

    const start = textarea.selectionStart || 0
    const end = textarea.selectionEnd || 0
    const text = localMessage.value || ''
    console.log('Insert asterisk - start:', start, 'end:', end, 'text:', text)

    // 检查光标是否在空的 ** 内
    if (start === end && start > 0 && start < text.length) {
        const charBefore = text[start - 1]
        const charAfter = text[start]
        if (charBefore === '*' && charAfter === '*') {
            // 即使在空的 ** 内也要保持焦点
            textarea.focus()
            return
        }
    }

    const selectedText = text.substring(start, end)
    let newText = ''
    let newCursorPos = 0

    if (selectedText) {
        // 包裹选中的文本
        newText = text.substring(0, start) + '*' + selectedText + '*' + text.substring(end)
        newCursorPos = end + 2
    } else {
        // 在文本末尾添加 **
        newText = text + '**'
        newCursorPos = text.length + 1
    }

    chatStore.setCurrentMessage(newText)

    // 立即重新聚焦，确保键盘不会收起
    nextTick(() => {
        textarea.focus()
        textarea.setSelectionRange(newCursorPos, newCursorPos)
        autoResize()
    })
}

// iOS 键盘监听处理
const handleFocusIn = () => {
    emit('keyboardShow')
    isFocus.value = true
    setTimeout(() => {
        isFocus.value = false
    }, 200)

    // iOS 上延迟获取视口变化
    const distance = -10
    emit('updateDistance', distance)
}

const handleFocusOut = () => {
    emit('keyboardHide')
    const distance = 10
    emit('updateDistance', distance)
}

// 失焦时
const handleBlur = () => {
    isFocusing.value = false
    isExpanded.value = false
    isContentOverTwoLines.value = false // 失焦时重置状态
    placeholderText.value = t('message.placeholderBlur')
    const el = inputRef.value
    if (!el) return

    // 失焦时：根据内容调整高度
    if (localMessage.value.trim()) {
        // 有内容时，根据内容自动调整高度
        autoResize()
    } else {
        // 没有内容时，恢复最小高度
        el.style.height = '25px'
        el.style.overflowY = 'hidden'
    }
}

// 聚焦时
const handleFocus = () => {
    isFocusing.value = true
    placeholderText.value = t('message.placeholderFocus')
    const el = inputRef.value
    if (!el) return

    // 聚焦时：输入框变高，方便输入多行内容
    if (localMessage.value.trim()) {
        // 有内容时，根据内容自动调整高度
        autoResize()
    } else {
        // 没有内容时，设置一个合适的高度（约2-3行，50-60px），聚焦时变高
        el.style.height = '50px'
        el.style.overflowY = 'hidden' // 初始时隐藏滚动条，输入后会自动显示
    }
}

// 切换展开/收起状态
const toggleExpand = () => {
    isExpanded.value = !isExpanded.value
    if (isExpanded.value) {
        logEventSafe('typing_expand_click')
        autoResize()
    } else {
        logEventSafe('typing_shrink_click')
        nextTick(() => {
            autoResize()
        })
    }

}

// 当内容被清空时，自动恢复默认高度
watch(localMessage, (val) => {
    if (!val) {
        resetHeight()
    }
})

onMounted(async () => {
    // 监听输入框焦点事件
    const inputEl = inputRef.value
    if (inputEl) {
        inputEl.addEventListener('focus', handleFocusIn)
        inputEl.addEventListener('blur', handleFocusOut)
    }

})

onUnmounted(() => {
    // 移除键盘监听
    const inputEl = inputRef.value
    if (inputEl) {
        inputEl.removeEventListener('focus', handleFocusIn)
        inputEl.removeEventListener('blur', handleFocusOut)
    }
})

// 暴露方法给父组件
defineExpose({
    focus: () => inputRef.value?.focus(),
    blur: () => inputRef.value?.blur(),
    resetHeight
})
</script>

<style scoped lang="scss">
.active-within {
    background: rgba(1, 3, 27, 0.3) !important;
}

.expanded-input-container {
    width: 101% !important;
    bottom: -20px !important;
    border: none !important;

}

// 输入框样式
:deep(.message-center-footer-input) {
    border-radius: 23px;
    background: rgba(1, 3, 27, 0.1);
    transform: translateZ(0);
    // transition: all 0.2s ease;
    padding: 8px 13px;
    border: 1px solid rgba(255, 255, 255, 0.3);

    // 只在准备好后应用 backdrop-filter，避免初始渲染时变黑
    &.expanded-input {
        background: #01031B !important;
        border-radius: 16px 16px 0px 0px !important;
        padding: 20px !important;
        border: 1px solid #01031B;


    }

    &.backdrop-ready {
        -webkit-backdrop-filter: blur(10px);
        backdrop-filter: blur(10px);
        will-change: backdrop-filter;
        -webkit-backdrop-filter: blur(10px);
    }

    .message-center-footer-input-textarea {
        display: block;
        width: 100%;
        min-height: 22.5px;
        max-height: 300px;
        height: 22.5px;
        // margin-left: 4px;
        font-weight: 500;
        font-size: 14px;
        color: #FFFFFF !important;
        line-height: 1.4;
        flex: 1;
        resize: none;
        overflow-y: hidden;
        white-space: pre-wrap;
        word-break: break-word;
        background: transparent;
        border: none;
        outline: none;
    }

    .message-center-footer-input-textarea::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }
}

// "Change scene" 提示的戳一戳动画
.shake-fade-enter-active {
    animation: pokeAnimation 0.8s cubic-bezier(0.36, 0, 0.66, -0.56);
}

.shake-fade-leave-active {
    animation: pokeFadeOut 0.3s ease-in;
}

@keyframes pokeAnimation {
    0% {
        opacity: 0;
        transform: scale(0.3) rotate(0deg);
    }

    10% {
        opacity: 1;
        transform: scale(1.2) rotate(-5deg);
    }

    20% {
        transform: scale(0.9) rotate(5deg);
    }

    30% {
        transform: scale(1.1) rotate(-4deg);
    }

    40% {
        transform: scale(0.95) rotate(4deg);
    }

    50% {
        transform: scale(1.05) rotate(-3deg);
    }

    60% {
        transform: scale(0.98) rotate(3deg);
    }

    70% {
        transform: scale(1.02) rotate(-2deg);
    }

    80% {
        transform: scale(0.99) rotate(2deg);
    }

    90% {
        transform: scale(1.01) rotate(-1deg);
    }

    100% {
        opacity: 1;
        transform: scale(1) rotate(0deg);
    }
}

@keyframes pokeFadeOut {
    0% {
        opacity: 1;
        transform: scale(1) translateY(0);
    }

    100% {
        opacity: 0;
        transform: scale(0.8) translateY(-10px);
    }
}

.is-focusing {
    position: absolute;
    bottom: 10px;
    left: 13px;
}

.focusing-input {
    display: block;
    padding: 10px !important;
    border-radius: 16px !important;
}
</style>
